import { zodResolver } from '@hookform/resolvers/zod'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { clsx } from 'clsx'
import { useId } from 'react'
import { Helmet } from 'react-helmet-async'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { z } from 'zod'

import * as Box from ':/_console/js/components/Box.tsx'
import logoSmall from '{assets}/images/logo-small.svg'
import { AnimatedNotification } from '~/components/AsyncAnimatedNotification.tsx'
import AsyncButton from '~/components/AsyncButton.tsx'
import { Button } from '~/components/Button.tsx'
import * as CenteredBox from '~/components/CenteredBox.tsx'
import { Form, FormGroup } from '~/components/form/primitives.tsx'
import TextInput from '~/components/form/TextInput.tsx'
import { GitHubIcon } from '~/components/icons/github.tsx'
import { Text } from '~/components/Text.tsx'
import FlashMessages from '~/features/flash-messages/FlashMessages.tsx'
import useFlashMessages from '~/features/flash-messages/useFlashMessages.ts'
// import { tryLogoutIntercom } from '~/utils/auth.ts'

import { useLoginUser } from './data.ts'
import { useSignupStore } from './store.ts'
import styles from './styles.module.scss'

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(6).max(72),
})

type FieldValues = z.infer<typeof schema>

function LoginPage() {
  // const location = useLocation()
  const flashMessages = useFlashMessages()

  // make sure an email address is visible on the verify email page
  // when a user signs up and then logs in without verifying their email first
  const setEmail = useSignupStore((state) => state.setEmail)

  const loginUser = useLoginUser()

  const emailId = useId()
  const passwordId = useId()

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<FieldValues>({
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: zodResolver(schema),
  })

  return (
    <>
      <Helmet>
        <title>Login | Transloadit</title>
      </Helmet>

      <main className="tl-container">
        <CenteredBox.Section compact>
          <CenteredBox.Header>
            <CenteredBox.Logo>
              <a href="/">
                <img src={logoSmall} width="40" height="40" alt="Transloadit" />
              </a>
            </CenteredBox.Logo>
            <h1>Sign in to your account</h1>
          </CenteredBox.Header>

          <Box.Outer>
            <Box.Content>
              <Box.Section isSeparated className={styles.boxSection}>
                <Form
                  onSubmit={handleSubmit((data) => {
                    const { email, password } = data
                    setEmail(email)
                    loginUser.execute(email, password)
                  })}
                >
                  <FlashMessages flashMessages={flashMessages} />

                  <AnimatedNotification danger small showNotification={!!errors.root}>
                    {errors.root?.message}
                  </AnimatedNotification>

                  <AnimatedNotification
                    showNotification={loginUser.status === 'error'}
                    danger
                    small
                  >
                    {loginUser.error?.message}
                  </AnimatedNotification>

                  <AnimatedNotification
                    showNotification={loginUser.status === 'success'}
                    success
                    small
                  >
                    {loginUser.result?.meta.message}
                  </AnimatedNotification>

                  <Button asChild variant="github" size="large" className={styles.fullWidthButton}>
                    <a href="/signin-with-github/">
                      <GitHubIcon aria-hidden />
                      <span>Log in with GitHub</span>
                    </a>
                  </Button>

                  <div className={styles.separator}>or</div>

                  <FormGroup>
                    <VisuallyHidden>
                      <label htmlFor={emailId}>Email</label>
                    </VisuallyHidden>
                    <TextInput
                      id={emailId}
                      error={errors?.email}
                      {...register('email')}
                      placeholder="Email"
                      type="email"
                      autoComplete="username"
                      required
                      autoFocus
                      naked
                      large
                    />
                  </FormGroup>

                  <FormGroup>
                    <VisuallyHidden>
                      <label htmlFor={passwordId}>Password</label>
                    </VisuallyHidden>
                    <TextInput
                      id={passwordId}
                      error={errors?.password}
                      {...register('password')}
                      placeholder="Password"
                      type="password"
                      autoComplete="current-password"
                      minLength={6}
                      maxLength={72}
                      naked
                      required
                      large
                    />
                  </FormGroup>

                  <AsyncButton
                    status={loginUser.status}
                    id="LoginPageUserButton"
                    className={clsx('btn', 'btn--primary', 'btn--large', styles.fullWidthButton)}
                    type="submit"
                  >
                    Log in
                  </AsyncButton>
                </Form>
              </Box.Section>

              <Box.Section className={clsx(styles.boxSection, styles.footer)}>
                <Link to="/c/forgot-password/">Forgot password?</Link>
              </Box.Section>
            </Box.Content>
          </Box.Outer>

          <CenteredBox.Extra>
            <Text>
              Don’t have an account?{' '}
              <Text asChild>
                <strong>
                  <Link to="/c/signup/">Sign up ›</Link>
                </strong>
              </Text>
            </Text>
          </CenteredBox.Extra>
        </CenteredBox.Section>
      </main>
    </>
  )
}

export { LoginPage as Component }
